import './TQConnectScoreCard.scss';

import React from 'react';

import { FaLightbulb } from 'react-icons/fa6';

import { useTheme } from '../../../../context/ThemeProvider';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import { tintColor } from '../../../../shared/utils';
import {
  ITQBadges,
  TQBadgeColorType,
} from '../../../../types/tqConnectTypes';
import { TQ_BADGE_COLORS_MAP } from '../../constants';
import TQConnectBadge from '../TQConnectBadge';
import TQConnectIcon from '../TQConnectIcon';
import TQScoreWheel from './TQScoreWheel';
import TQScoreWheelSmall from './TQScoreWheelSmall';

interface TQConnectScoreCardProps {
  score: number;
  className?: string;
  showInfoIcon?: boolean;
  isOwnScore?: boolean;
  /** Small fixed 200×120px widget variant */
  variant?: 'default' | 'small';
}

const TQConnectScoreCard: React.FC<TQConnectScoreCardProps> = ({
  score,
  className = '',
  showInfoIcon = false,
  isOwnScore = false,
  variant = 'default',
}) => {
  const { colors: COLORS } = useTheme();

  const { data: tqConnectConfigResult } = useTQConnectConfig();

  const tqConnectConfig = tqConnectConfigResult?.tqConnectConfig;
  const badges: ITQBadges = tqConnectConfig?.badges || {};

  // Function to get badge ranges for the wheel segments { min, max, badge, color, fillColor }
  const getBadgeRanges = () => {
    // make a list of badge thresholds in ascending order (lowest to highest score)
    const badgeThresholds = Object.keys(badges)
      .map(Number)
      .sort((a, b) => a - b);

    return badgeThresholds.map((threshold, index) => {
      const badge = badges[String(threshold)];

      return {
        min: badgeThresholds[index - 1] + 1 || 0,
        max: threshold,
        badge,
        color:
          TQ_BADGE_COLORS_MAP[badge.color as TQBadgeColorType].COLOR_HIGHLIGHT,
        fillColor: badge.color,
      };
    });
  };

  if (score === undefined || score === null) {
    return null;
  }

  const isSmall = variant === 'small';

  return (
    <div
      className={`tq-score-card ${isSmall ? 'tq-score-card--small' : ''} ${className}`}>
      {showInfoIcon && !isSmall ? (
        <div
          className="card__lightbulb"
          style={{ backgroundColor: tintColor(COLORS.WARNING, 90) }}>
          <FaLightbulb size={24} color={COLORS.WARNING} />
        </div>
      ) : null}

      <div className="card__progress-wheel">
        <div className="progress-wheel__canvas-wrap">
          {isSmall ? (
            <TQScoreWheelSmall
              value={score}
              maxScore={tqConnectConfig?.scoreConfig?.max || 1000}
              levels={getBadgeRanges()}
              trackColor={COLORS.BACKGROUND}
            />
          ) : (
            <TQScoreWheel
              value={score}
              maxScore={tqConnectConfig?.scoreConfig?.max || 1000}
              size={300}
              thickness={16}
              levels={getBadgeRanges()}
              trackColor={COLORS.BACKGROUND}
            />
          )}
        </div>

        <div className="progress-wheel__content">
          <div className="content__title">
            {isOwnScore ? 'YOUR' : ''} {tqConnectConfig?.scoreConfig?.name}{' '}
            SCORE
          </div>

          <div className="content__score">
            <div className="score__icon">
              <TQConnectIcon size={isSmall ? 18 : undefined} />
            </div>
            <div className="score__value">{score}</div>
          </div>
        </div>
      </div>

      <TQConnectBadge
        score={score}
        iconSize="sm"
        style={{
          marginTop: isSmall ? -8 : -50,
        }}
      />
    </div>
  );
};

export default TQConnectScoreCard;
